import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Building2, Banknote, FolderUp, ArrowLeft, Upload, Trash2, Save, FileText, Image as ImageIcon, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FOURNITURE_ROWS = [
    { key: 'carton_standard', label: 'Carton standard (€)' },
    { key: 'carton_livre', label: 'Carton livre (€)' },
    { key: 'carton_fragile', label: 'Carton fragile (€)' },
    { key: 'carton_bouteille', label: 'Carton bouteille (€)' },
    { key: 'carton_verre', label: 'Carton verre (€)' },
    { key: 'adhesif', label: 'Adhésif (€)' },
    { key: 'bulle_100m', label: 'Bulle 100m (€)' },
    { key: 'bulle_10m', label: 'Bulle 10m (€)' },
];

export default function Parametres() {
    const navigate = useNavigate();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingCompany, setSavingCompany] = useState(false);
    const [savingTarifs, setSavingTarifs] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    const [company, setCompany] = useState({});
    const [tarifs, setTarifs] = useState(null);
    const logoInputRef = useRef(null);
    const fileInputRef = useRef(null);
    const [fileLabel, setFileLabel] = useState('');

    useEffect(() => { fetchSettings(); }, []);

    const fetchSettings = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/settings`, { withCredentials: true });
            setSettings(res.data);
            setCompany(res.data.company || {});
            setTarifs(res.data.tarifs || {});
        } catch {
            toast.error('Impossible de charger les paramètres');
        } finally {
            setLoading(false);
        }
    };

    // ============ COMPANY ============
    const saveCompany = async () => {
        setSavingCompany(true);
        try {
            const res = await axios.put(`${API_URL}/api/settings/company`, company, { withCredentials: true });
            setSettings(res.data);
            toast.success('Coordonnées enregistrées');
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
        } finally {
            setSavingCompany(false);
        }
    };

    const onLogoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingLogo(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await axios.post(`${API_URL}/api/settings/logo`, fd, {
                withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
            });
            setCompany(prev => ({ ...prev, logo_url: res.data.logo_url }));
            toast.success('Logo mis à jour');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur upload logo');
        } finally {
            setUploadingLogo(false);
            if (logoInputRef.current) logoInputRef.current.value = '';
        }
    };

    // ============ TARIFS ============
    const updateTarif = (section, keyOrFormule, val) => {
        setTarifs(prev => {
            const next = { ...prev };
            if (section === 'root') {
                next[keyOrFormule] = val;
            } else {
                next[section] = { ...(next[section] || {}), [keyOrFormule]: val };
            }
            return next;
        });
    };

    const saveTarifs = async () => {
        setSavingTarifs(true);
        try {
            const res = await axios.put(`${API_URL}/api/settings/tarifs`, tarifs, { withCredentials: true });
            setSettings(res.data);
            toast.success('Tarifs enregistrés');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setSavingTarifs(false);
        }
    };

    // ============ FILES ============
    const onFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingFile(true);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('label', fileLabel);
        try {
            await axios.post(`${API_URL}/api/settings/files`, fd, {
                withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
            });
            setFileLabel('');
            fetchSettings();
            toast.success('Fichier ajouté');
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const deleteFile = async (id) => {
        if (!window.confirm('Supprimer ce fichier ?')) return;
        try {
            await axios.delete(`${API_URL}/api/settings/files/${id}`, { withCredentials: true });
            fetchSettings();
            toast.success('Fichier supprimé');
        } catch {
            toast.error('Erreur');
        }
    };

    const humanSize = (n) => {
        if (!n) return '';
        if (n < 1024) return `${n} o`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
        return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
    };

    if (loading || !settings) {
        return (
            <Layout>
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B365D]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 pb-12" data-testid="parametres-page">
                {/* Header */}
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate('/administration')}
                            className="w-9 h-9 rounded-full bg-white border border-gray-200 hover:border-[#1B365D]/40 flex items-center justify-center transition-colors"
                            data-testid="back-to-admin"
                        >
                            <ArrowLeft className="w-4 h-4 text-[#1B365D]" />
                        </button>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Paramètres
                            </h1>
                            <p className="text-gray-500 text-sm mt-0.5">Configuration globale du CRM</p>
                        </div>
                    </div>
                </div>

                {/* BLOC 1 — Tarifs */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="param-tarifs">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                            <Banknote className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-[#1B365D]">Formules / Tarifs</h2>
                            <p className="text-xs text-gray-500">Valeurs par défaut utilisées dans les nouveaux devis</p>
                        </div>
                        <Button size="sm" className="ml-auto bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-1.5" onClick={saveTarifs} disabled={savingTarifs} data-testid="save-tarifs-btn">
                            <Save className="w-3.5 h-3.5" />
                            {savingTarifs ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </div>
                    <div className="p-5 space-y-5">
                        {/* Global tarifs */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs font-medium text-gray-600">Tarif au km (€)</Label>
                                <Input
                                    type="number" step="0.01" min="0"
                                    value={tarifs?.tarif_km ?? ''}
                                    onChange={e => updateTarif('root', 'tarif_km', e.target.value)}
                                    className="mt-1"
                                    data-testid="tarif-km"
                                />
                            </div>
                        </div>
                        {/* Fournitures */}
                        <div>
                            <h3 className="text-sm font-semibold text-[#1B365D] mb-2">Fournitures (prix unitaires)</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                {FOURNITURE_ROWS.map(f => (
                                    <div key={f.key}>
                                        <Label className="text-xs text-gray-600">{f.label}</Label>
                                        <Input
                                            type="number" step="0.01" min="0"
                                            value={tarifs?.fournitures?.[f.key] ?? ''}
                                            onChange={e => updateTarif('fournitures', f.key, e.target.value)}
                                            className="mt-1 h-8"
                                            data-testid={`fourniture-${f.key}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* BLOC 2 — Coordonnées */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="param-company">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-[#1B365D]">Coordonnées entreprise</h2>
                            <p className="text-xs text-gray-500">Utilisées sur les PDF devis, factures et lettres de voiture</p>
                        </div>
                        <Button size="sm" className="ml-auto bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-1.5" onClick={saveCompany} disabled={savingCompany} data-testid="save-company-btn">
                            <Save className="w-3.5 h-3.5" />
                            {savingCompany ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </div>
                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs text-gray-600">Nom entreprise</Label>
                            <Input value={company.nom || ''} onChange={e => setCompany({ ...company, nom: e.target.value })} className="mt-1" data-testid="company-nom" />
                        </div>
                        <div>
                            <Label className="text-xs text-gray-600">SIRET</Label>
                            <Input value={company.siret || ''} onChange={e => setCompany({ ...company, siret: e.target.value })} className="mt-1" data-testid="company-siret" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs text-gray-600">Adresse</Label>
                            <Input value={company.adresse || ''} onChange={e => setCompany({ ...company, adresse: e.target.value })} className="mt-1" data-testid="company-adresse" />
                        </div>
                        <div>
                            <Label className="text-xs text-gray-600">Email</Label>
                            <Input type="email" value={company.email || ''} onChange={e => setCompany({ ...company, email: e.target.value })} className="mt-1" data-testid="company-email" />
                        </div>
                        <div>
                            <Label className="text-xs text-gray-600">Téléphone</Label>
                            <Input value={company.telephone || ''} onChange={e => setCompany({ ...company, telephone: e.target.value })} className="mt-1" placeholder="Ex: 01 89 16 83 08" data-testid="company-telephone" />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Numéro affiché sur les devis, factures, lettres de voiture et signatures d'email.
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs text-gray-600">Capital</Label>
                            <Input value={company.capital || ''} onChange={e => setCompany({ ...company, capital: e.target.value })} className="mt-1" data-testid="company-capital" />
                        </div>
                        <div>
                            <Label className="text-xs text-gray-600">Site web</Label>
                            <Input value={company.site_web || ''} onChange={e => setCompany({ ...company, site_web: e.target.value })} className="mt-1" data-testid="company-site" />
                        </div>
                        <div className="md:col-span-2 flex items-center gap-4 pt-2 border-t border-gray-100 mt-2">
                            <div className="w-20 h-20 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                                {company.logo_url ? (
                                    <img src={`${API_URL}${company.logo_url}`} alt="logo" className="max-w-full max-h-full object-contain" />
                                ) : (
                                    <ImageIcon className="w-6 h-6 text-gray-400" />
                                )}
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-[#1B365D]">Logo de l'entreprise</p>
                                <p className="text-xs text-gray-500">png, jpg, webp ou svg</p>
                                <div className="mt-2 flex gap-2">
                                    <input ref={logoInputRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={onLogoChange} className="hidden" />
                                    <Button size="sm" variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="company-logo-upload">
                                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                                        {uploadingLogo ? 'Envoi...' : (company.logo_url ? 'Changer' : 'Téléverser')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                {/* BLOC 3 — Fichiers */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="param-files">
                    <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                            <FolderUp className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-[#1B365D]">Fichiers partagés</h2>
                            <p className="text-xs text-gray-500">Attestation assurance, CGV, catalogue... cochables à l'envoi d'un devis</p>
                        </div>
                    </div>
                    <div className="p-5 space-y-4">
                        <div className="flex flex-col sm:flex-row gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <Input
                                placeholder="Libellé du fichier (ex: Attestation d'assurance 2026)"
                                value={fileLabel}
                                onChange={(e) => setFileLabel(e.target.value)}
                                className="flex-1"
                                data-testid="file-label-input"
                            />
                            <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" onChange={onFileChange} className="hidden" />
                            <Button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingFile}
                                className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white gap-1.5"
                                data-testid="upload-file-btn"
                            >
                                <Upload className="w-4 h-4" />
                                {uploadingFile ? 'Envoi...' : 'Ajouter'}
                            </Button>
                        </div>

                        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="files-list">
                            {(settings.files || []).length === 0 ? (
                                <div className="py-8 text-center text-sm text-gray-500">
                                    Aucun fichier. Ajoute une attestation ou un document que les commerciaux pourront joindre aux devis.
                                </div>
                            ) : (
                                settings.files.map(f => (
                                    <div key={f.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50/50" data-testid={`file-row-${f.id}`}>
                                        <FileText className="w-5 h-5 text-[#1B365D] shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-[#1B365D] truncate">{f.label}</p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {f.original_name} • {humanSize(f.size)} • {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString('fr-FR') : ''}
                                            </p>
                                        </div>
                                        <a
                                            href={`${API_URL}${f.url}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-gray-500 hover:text-[#1B365D] transition-colors p-2"
                                            data-testid={`file-download-${f.id}`}
                                        >
                                            <Download className="w-4 h-4" />
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => deleteFile(f.id)}
                                            className="text-red-500 hover:bg-red-50 rounded p-2 transition-colors"
                                            data-testid={`file-delete-${f.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
